import API, { responseOf } from "./base";
import { Organization, User } from "../model";
import FieldDefinition from "../model/FieldDefinition";
import OrganizationBillingState, {
  StripeCustomerCreation,
  StripeCustomerStatus,
  StripeMeterData,
  StripeValidation,
} from "../model/OrganizationBillingState";
import { OrganizationMember } from "../model/User";

export const create = (organization: Organization): Promise<Organization> =>
  responseOf(API.post("/organizations", organization));

export const get = (organizationId: string): Promise<Organization> =>
  responseOf(API.get(`/organizations/${organizationId}`, { public: true }));

export const byKey = (organizationKey: string): Promise<Organization> =>
  responseOf(
    API.get(`/organizations/byKey/${organizationKey}`, { public: true }),
  );

export const update = (organization: Organization): Promise<Organization> =>
  responseOf(API.put(`/organizations/${organization.id}`, organization));

const remove = (organizationId: string): Promise<void> =>
  API.delete(`/organizations/${organizationId}`);
export { remove as delete };

export const byOwner = (userId: string): Promise<Organization[]> =>
  responseOf(API.get(`/organizations/byOwner/${userId}`));

export const getMembers = (
  organizationId: string,
  filter?: string,
): Promise<User[]> =>
  responseOf(
    API.get(`/organizations/${organizationId}/users`, {
      params: { filter },
    }),
  );

export const getMeteredSubscriptionInfo = (
  organizationId: string,
  startDate?: Date,
  endDate?: Date,
): Promise<StripeMeterData> =>
  responseOf(
    API.get(`/organizations/${organizationId}/tracker/meter`, {
      params: {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      },
    }),
  );

export const getBillingState = (
  organizationId: string,
): Promise<OrganizationBillingState> =>
  responseOf(API.get(`/organizations/${organizationId}/billing`));

export const getSubscriptions = (
  organizationId: string,
): Promise<StripeCustomerStatus> =>
  responseOf(API.get(`/organizations/${organizationId}/subscriptions`));

export const getBillingPortalUrl = (
  organizationId: string,
  returnPath: string,
): Promise<string> =>
  responseOf(
    API.get(`/organizations/${organizationId}/billing-portal`, {
      params: { returnPath },
    }),
  );

export const forceRefreshSubscription = (
  organizationId: string,
): Promise<boolean> =>
  responseOf(API.post(`/organizations/${organizationId}/refresh-subscription`));

export const createStripeCustomer = async (
  organizationId: string,
  customer: StripeCustomerCreation,
): Promise<void> => {
  await API.post(`/organizations/${organizationId}/stripe`, customer);
};

export const validateStripeId = (
  organizationId: string,
  stripeId: string,
): Promise<StripeValidation> =>
  responseOf(
    API.get(`/organizations/${organizationId}/stripe/validate/${stripeId}`),
  );

export const updateStripeId = (
  organizationId: string,
  stripeId: string | null,
): Promise<Organization> =>
  API.put(`/organizations/${organizationId}/stripe`, { stripeId });

export const byMember = (userId: string): Promise<Organization[]> =>
  responseOf(API.get(`/organizations/byMember/${userId}`));

export const byMemberOrAccountUser = (
  userId: string,
): Promise<Organization[]> =>
  responseOf(API.get(`/organizations/byMemberOrAccountUser/${userId}`));

export const removeMember = async (
  organizationId: string,
  userId: string,
): Promise<void> => {
  await API.delete(`/organizations/${organizationId}/users/${userId}`);
};

export const fromWebsite = (website: string): Promise<Organization> =>
  responseOf(API.post("/organizations/fromWebsite", { url: website }));

export const confirmEmail = async (
  organizationId: string,
  confirmationCode: string,
): Promise<void> => {
  await API.post(`/email-consumer/${organizationId}/confirm`, undefined, {
    params: { code: confirmationCode },
  });
};

export const byOrgRole = (orgRoleId: string): Promise<Organization[]> =>
  responseOf(API.get(`/organizations/byOrgRole/${orgRoleId}`));

export const getFieldDefinitions = (
  organizationId: string,
): Promise<{
  selected: string[];
  available: Record<string, FieldDefinition>;
}> => responseOf(API.get(`/organizations/${organizationId}/fields`));

export const addSelectedFieldDefinition = async (
  organizationId: string,
  fieldDefinitionId: string,
): Promise<void> => {
  await API.post(
    `/organizations/${organizationId}/fields/${fieldDefinitionId}`,
  );
};

export const removeSelectedFieldDefinition = async (
  organizationId: string,
  fieldDefinitionId: string,
): Promise<void> => {
  await API.delete(
    `/organizations/${organizationId}/fields/${fieldDefinitionId}`,
  );
};

export const setSelectedFieldDefinitions = async (
  organizationId: string,
  fieldDefinitionIds: string[],
): Promise<void> => {
  await API.put(`/organizations/${organizationId}/fields`, fieldDefinitionIds);
};

export const search = (query: string): Promise<Organization[]> =>
  responseOf(API.post("/organizations/search", { query }));

export const searchMembers = (
  query: string,
  organizationId?: string,
): Promise<OrganizationMember[]> =>
  responseOf(
    API.post(
      `/organizations/members/search${organizationId ? `/organization/${organizationId}` : ""}`,
      { query },
    ),
  );

export const refreshMembers = async (organizationId: string): Promise<void> => {
  await API.post(`/organizations/${organizationId}/members/refresh`);
};

export const getVisibility = async (
  organizationId: string,
  userId: string,
): Promise<boolean> => {
  const result = await responseOf<{ visibility: boolean }>(
    API.get(
      `/organizations/members/${userId}/organization/${organizationId}/visibility`,
    ),
  );
  return result.visibility;
};

export const setVisibility = async (
  organizationId: string,
  userId: string,
  visibility: boolean,
): Promise<void> => {
  await API.post(
    `/organizations/members/${userId}/organization/${organizationId}/${visibility ? "enable" : "disable"}`,
  );
};
